/**
 * eBay delisting — the double-sell safety net.
 *
 * endEbayListingsForLots(): fire-and-forget after any in-app disposition
 * (sale / trade / gift / write-off). Checks the disposed card lots against
 * live ebay_listings rows (select-own RLS) and ends any listing whose listed
 * qty can no longer be covered by the remaining on-hand qty. Never blocks or
 * fails the transaction save.
 *
 * endEbayListingManual(): the drawer badge's "End listing" button.
 */
import { sb } from "./supabase";
import { DB } from "./db";
import { toast } from "./dom";

export async function endEbayListingsForLots(lotIds: string[], reason: string): Promise<void> {
  try {
    const ids = [...new Set(lotIds.filter(id => id && (id.startsWith("C-") || id.startsWith("S-"))))];
    if (!ids.length) return;
    const { data } = await sb.from("ebay_listings")
      .select("id, lot_id, sku, listing_id, qty")
      .in("lot_id", ids).eq("status", "published");
    const rows = (data || []) as Array<{ id: number; lot_id: string; sku: string; listing_id: string | null; qty: number | null }>;
    for (const r of rows) {
      const lot = r.lot_id.startsWith("S-")
        ? DB.table("sealed").find(x => x["Lot ID"] === r.lot_id)
        : DB.table("cards").find(c => c["Card Lot ID"] === r.lot_id);
      const remaining = Number(lot?.["Qty"]) || 0;
      const listedQty = Number(r.qty) || 1;
      if (remaining >= listedQty) continue; // still enough on hand to cover it
      const { data: res, error } = await sb.functions.invoke("ebay-listing", {
        body: { action: "end", listingRowId: r.id, reason },
      });
      if (error || (res as any)?.error) {
        toast(`Could not auto-end eBay listing #${r.listing_id || r.sku} \u2014 please end it on eBay`, "err");
      } else {
        toast(`eBay listing #${r.listing_id || r.sku} ended \u2014 ${reason}`);
      }
    }
  } catch (e) {
    console.warn("[ebayDelist]", e);
  }
}

/** Manual end from the lot drawer. Returns true on success. */
export async function endEbayListingManual(lotId: string): Promise<boolean> {
  if (!confirm("End this eBay listing? Buyers will no longer see it. (You can list the lot again later.)")) return false;
  try {
    const { data } = await sb.from("ebay_listings")
      .select("id, listing_id").eq("lot_id", lotId).eq("status", "published")
      .order("id", { ascending: false }).limit(1);
    const row = (data || [])[0] as { id: number; listing_id: string | null } | undefined;
    if (!row) { toast("No live eBay listing found for this lot", "err"); return false; }
    const { data: res, error } = await sb.functions.invoke("ebay-listing", {
      body: { action: "end", listingRowId: row.id, reason: "ended manually" },
    });
    if (error || (res as any)?.error) throw new Error(String((res as any)?.error || error));
    const card = DB.table("cards").find(c => c["Card Lot ID"] === lotId);
    const sealed = card ? null : DB.table("sealed").find(x => x["Lot ID"] === lotId);
    if (card && String(card["Status"]) === "Listed") {
      DB.update("cards", "Card Lot ID", lotId, { "Status": "Available" });
    } else if (sealed && String(sealed["Status"]) === "Listed") {
      DB.update("sealed", "Lot ID", lotId, { "Status": "Available" });
    }
    toast(`eBay listing #${row.listing_id || ""} ended`);
    return true;
  } catch (e: any) {
    toast("End failed: " + (e?.message || e), "err");
    return false;
  }
}
